import { useEffect, useMemo, useState } from "react";
import { commentApi } from "@/api";
import { resolveCustomerAssetUrl } from "@/lib/asset";

const DEFAULT_THERAPIST_AVATAR =
  "https://readdy.ai/api/search-image?query=young%20asian%20massage%20therapist%20portrait%20professional%20uniform%20clean%20background&width=120&height=120&seq=customer-review-avatar&orientation=squarish";
const DEFAULT_ORDER_IMAGE =
  "https://readdy.ai/api/search-image?query=massage%20service%20spa%20professional%20indoors%20warm%20light%20clean%20high%20quality&width=240&height=240&seq=customer-review-goods&orientation=squarish";
const COMMENT_TAG_OPTIONS = ["准时上门", "手法专业", "力度合适", "服务细致", "沟通顺畅", "缓解明显", "态度很好", "值得推荐"];

interface OrderReviewModalProps {
  orderGoodsId: number;
  therapistName: string;
  therapistAvatar?: string;
  serviceName: string;
  serviceImage?: string;
  onClose: () => void;
  onSuccess?: () => Promise<void> | void;
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

export default function OrderReviewModal({
  orderGoodsId,
  therapistName,
  therapistAvatar,
  serviceName,
  serviceImage,
  onClose,
  onSuccess,
}: OrderReviewModalProps) {
  const [stars, setStars] = useState(5);
  const [text, setText] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof commentApi.getCommentGoodsInfo>> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadCommentGoodsInfo = async () => {
      setLoading(true);
      setError("");
      try {
        const result = await commentApi.getCommentGoodsInfo(orderGoodsId);
        if (cancelled) {
          return;
        }
        setDetail(result);
        if (result?.comment && typeof result.comment === "object") {
          const comment = result.comment as { service_comment?: number; comment?: string; tags?: string[] };
          if (comment.service_comment) {
            setStars(Number(comment.service_comment));
          }
          if (comment.comment) {
            setText(String(comment.comment));
          }
          if (Array.isArray(comment.tags)) {
            setSelectedTags(comment.tags.map((tag) => String(tag || "").trim()).filter(Boolean));
          }
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(getErrorMessage(requestError, "评价信息加载失败，请稍后重试"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadCommentGoodsInfo();

    return () => {
      cancelled = true;
    };
  }, [orderGoodsId]);

  const displayTherapistName = useMemo(
    () => detail?.coach_info?.name || therapistName || "待评价技师",
    [detail, therapistName],
  );
  const displayTherapistAvatar = useMemo(
    () =>
      resolveCustomerAssetUrl(detail?.coach_info?.work_photo) ||
      resolveCustomerAssetUrl(therapistAvatar) ||
      DEFAULT_THERAPIST_AVATAR,
    [detail, therapistAvatar],
  );
  const displayServiceName = useMemo(
    () => detail?.goods_snap?.name || serviceName || "服务项目",
    [detail, serviceName],
  );
  const displayServiceImage = useMemo(
    () =>
      resolveCustomerAssetUrl(detail?.goods_snap?.image) ||
      resolveCustomerAssetUrl(serviceImage) ||
      DEFAULT_ORDER_IMAGE,
    [detail, serviceImage],
  );

  const toggleTag = (tag: string) => {
    setSelectedTags((current) => (
      current.includes(tag)
        ? current.filter((item) => item !== tag)
        : [...current, tag].slice(0, 6)
    ));
  };

  const handleSubmit = async () => {
    if (submitting || loading) {
      return;
    }
    setSubmitting(true);
    setError("");

    try {
      await commentApi.submitComment({
        order_goods_id: orderGoodsId,
        service_comment: stars,
        comment: text.trim(),
        tags: selectedTags,
      });
      await onSuccess?.();
      setSubmitted(true);
    } catch (requestError) {
      setError(getErrorMessage(requestError, "提交评价失败，请稍后重试"));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 z-[80] flex items-center justify-center" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="w-[90%] rounded-3xl bg-white px-8 py-10" style={{ maxWidth: 320 }}>
          <div className="flex flex-col items-center">
            <div
              className="mb-4 flex h-16 w-16 items-center justify-center rounded-full"
              style={{ background: "linear-gradient(135deg, #FF8C3A, #FF5C00)" }}
            >
              <i className="ri-check-line text-3xl text-white" />
            </div>
            <p className="mb-1 text-base font-bold" style={{ color: "#1A1A1A" }}>
              评价成功
            </p>
            <p className="mb-6 text-sm" style={{ color: "#AAAAAA" }}>
              感谢你的反馈，帮助技师提供更好的服务体验
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-full py-3 text-sm font-bold text-white cursor-pointer whitespace-nowrap"
            style={{ background: "linear-gradient(90deg, #FF8C3A, #FF5C00)" }}
          >
            完成
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div
        className="w-full rounded-t-3xl bg-white px-5 pb-8 pt-5"
        style={{ maxWidth: 480, animation: "slideUp 0.25s ease" }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <span className="text-base font-bold" style={{ color: "#1A1A1A" }}>
            评价服务
          </span>
          <button type="button" onClick={onClose} className="cursor-pointer">
            <i className="ri-close-line text-xl" style={{ color: "#999" }} />
          </button>
        </div>

        {error ? (
          <div className="mb-4 rounded-2xl px-4 py-3 text-sm" style={{ background: "#FFF4EC", color: "#D94A00", border: "1px solid #FFD8BF" }}>
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="flex items-center gap-3 rounded-2xl px-4 py-5" style={{ background: "#FFF9F4", border: "1px solid #F3E7DB" }}>
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-t-transparent" style={{ borderColor: "#F08A3D transparent #F08A3D #F08A3D" }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: "#1A1A1A" }}>
                正在加载评价信息
              </p>
              <p className="mt-1 text-xs" style={{ color: "#AAAAAA" }}>
                稍后就可以提交本次服务评价
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-4 flex items-center gap-3 rounded-2xl px-3 py-3" style={{ background: "#FFF9F4", border: "1px solid #F3E7DB" }}>
              <div className="h-14 w-14 overflow-hidden rounded-2xl flex-shrink-0">
                <img src={displayServiceImage} alt={displayServiceName} className="h-full w-full object-cover object-top" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 text-sm font-semibold leading-snug" style={{ color: "#1A1A1A" }}>
                  {displayServiceName}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <div className="h-8 w-8 overflow-hidden rounded-full flex-shrink-0">
                    <img src={displayTherapistAvatar} alt={displayTherapistName} className="h-full w-full object-cover object-top" />
                  </div>
                  <span className="text-xs font-semibold" style={{ color: "#666" }}>
                    {displayTherapistName}
                  </span>
                </div>
              </div>
            </div>

            <div className="mb-5 flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((value) => (
                <button key={value} type="button" onClick={() => setStars(value)} className="flex h-8 w-8 items-center justify-center cursor-pointer">
                  <i
                    className={value <= stars ? "ri-star-fill" : "ri-star-line"}
                    style={{ fontSize: 28, color: value <= stars ? "#FFAA00" : "#DDD" }}
                  />
                </button>
              ))}
              <span className="ml-1 text-sm font-semibold" style={{ color: "#FF7B22" }}>
                {["", "很差", "较差", "一般", "满意", "非常满意"][stars]}
              </span>
            </div>

            <div className="mb-4">
              <p className="mb-2 text-sm font-semibold" style={{ color: "#1A1A1A" }}>
                评价标签
              </p>
              <div className="flex flex-wrap gap-2">
                {COMMENT_TAG_OPTIONS.map((tag) => {
                  const active = selectedTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      className="rounded-full px-3 py-1.5 text-xs font-semibold transition-colors"
                      style={{
                        background: active ? "#FFF0E6" : "#F7F7F7",
                        border: active ? "1px solid #FF7B22" : "1px solid #EFEFEF",
                        color: active ? "#FF5C00" : "#666",
                      }}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>

            <textarea
              value={text}
              onChange={(event) => setText(event.target.value.slice(0, 200))}
              placeholder="分享你的体验，帮助更多用户找到合适的服务"
              className="w-full resize-none rounded-2xl p-3.5 text-sm outline-none"
              rows={4}
              style={{ background: "#F9F9F9", border: "1px solid #F0F0F0", color: "#333" }}
            />
            <p className="mb-4 mt-1 text-right text-xs" style={{ color: "#BBBBBB" }}>
              {text.length}/200
            </p>

            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={submitting}
              className="w-full rounded-full py-3.5 text-sm font-bold text-white cursor-pointer whitespace-nowrap"
              style={{
                background: submitting ? "#E0E0E0" : "linear-gradient(90deg, #FF8C3A, #FF5C00)",
                color: submitting ? "#BBBBBB" : "#fff",
              }}
            >
              {submitting ? "提交中..." : "提交评价"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
